// MERGE branch edit planners. Targets `MergeBranch.condition` and the nested
// `MergeBranch.updateAssignments` for `WHEN [NOT] MATCHED THEN UPDATE SET …`
// arms — distinct from the top-level `QueryStructure.updateAssignments` ops
// (which are for top-level UPDATE statements only).
//
// Branch selection: `branchKind: 'matched' | 'not-matched'` + `branchIndex`
// pick the branch within `mergeWhenMatched[]` / `mergeWhenNotMatched[]` of
// the structure. The renderer (which iterates branches in source order) maps
// flat indices to (branchKind, branchIndex) at the call boundary.
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — DML edits (MERGE).

import type {
  MergeBranch,
  PlannedByteEdit,
  StructuralEdit,
} from '../types';
import type { MergeBranchKind } from '../types/structuralEdit';
import { rangeFromOffsets } from '../../sql-grammar/document/lineIndex';

import type { EditCtx } from './applyStructuralEdit';
import { planListInsert, planListRemove } from './applyStructuralEdit.lists';
import { scanTopLevelKeywords } from '../structure/scanner';
import { findValueByteRange } from './applyStructuralEdit.modify.update';
import type { ListItem } from './applyStructuralEdit.shared';

type MergeBranchEdit = Extract<
  StructuralEdit,
  {
    op:
      | 'modify-merge-branch-condition'
      | 'modify-merge-update-assignment'
      | 'add-merge-update-assignment'
      | 'remove-merge-update-assignment';
  }
>;

export function planMergeBranchEdit(
  ctx: EditCtx,
  edit: MergeBranchEdit,
): readonly PlannedByteEdit[] {
  const branch = pickBranch(ctx, edit.branchKind, edit.branchIndex);
  if (!branch) return [];
  switch (edit.op) {
    case 'modify-merge-branch-condition':
      return planConditionEdit(ctx, branch, edit.newCondition);
    case 'modify-merge-update-assignment':
      return planAssignmentValueEdit(ctx, branch, edit.assignmentIndex, edit.newValueText);
    case 'add-merge-update-assignment':
      return planAssignmentAdd(ctx, branch, edit.target, edit.valueText);
    case 'remove-merge-update-assignment':
      return planAssignmentRemove(ctx, branch, edit.assignmentIndex);
  }
}

// Exported for applyStructuralEdit.mergeBranches.ts (branch add/remove/action
// planners) — same (branchKind, branchIndex) addressing, shared resolution.
export function pickBranch(
  ctx: EditCtx,
  kind: MergeBranchKind,
  index: number,
): MergeBranch | null {
  const list = kind === 'matched'
    ? ctx.structure.mergeWhenMatched
    : ctx.structure.mergeWhenNotMatched;
  if (!list || index < 0 || index >= list.length) return null;
  return list[index];
}

// modify-merge-branch-condition --------------------------------------------
// Replaces the text after `AND` and before `THEN` inside the branch range.
function planConditionEdit(
  ctx: EditCtx,
  branch: MergeBranch,
  newCondition: string,
): readonly PlannedByteEdit[] {
  const v = newCondition.trim();
  if (v.length === 0) return [];
  const range = findConditionByteRange(ctx, branch);
  if (!range) return [];
  return [{
    range: rangeFromOffsets(ctx.lineIdx, range.start, range.end),
    replacement: v,
    rationale: 'modify-merge-branch-condition',
  }];
}

function findConditionByteRange(
  ctx: EditCtx,
  branch: MergeBranch,
): { start: number; end: number } | null {
  const start = branch.range.startOffset;
  const end = branch.range.endOffset;
  const kws = scanTopLevelKeywords(ctx.source, start, end, ctx.options);
  const andKw = kws.find((k) => k.upper === 'AND');
  const thenKw = kws.find((k) => k.upper === 'THEN');
  if (!andKw || !thenKw || andKw.start >= thenKw.start) return null;
  // Trim leading + trailing whitespace inside the (and.end, then.start) span.
  let s = andKw.end;
  let e = thenKw.start;
  while (s < e && /\s/.test(ctx.source[s])) s++;
  while (e > s && /\s/.test(ctx.source[e - 1])) e--;
  if (e <= s) return null;
  return { start: s, end: e };
}

// modify-merge-update-assignment -------------------------------------------
// Replaces the value text of the n-th nested SET assignment (`col = value`).
function planAssignmentValueEdit(
  ctx: EditCtx,
  branch: MergeBranch,
  index: number,
  newValueText: string,
): readonly PlannedByteEdit[] {
  const v = newValueText.trim();
  if (v.length === 0) return [];
  const assigns = branch.updateAssignments ?? [];
  if (index < 0 || index >= assigns.length) return [];
  const a = assigns[index];
  const valueRange = findValueByteRange(ctx.source, a, ctx.options);
  if (!valueRange) return [];
  return [{
    range: rangeFromOffsets(ctx.lineIdx, valueRange.start, valueRange.end),
    replacement: v,
    rationale: 'modify-merge-update-assignment',
  }];
}

// add-merge-update-assignment ----------------------------------------------
// Append `target = valueText` to the branch's SET list.
function planAssignmentAdd(
  ctx: EditCtx,
  branch: MergeBranch,
  target: string,
  valueText: string,
): readonly PlannedByteEdit[] {
  const t = target.trim();
  const v = valueText.trim();
  if (t.length === 0 || v.length === 0) return [];
  const assigns = branch.updateAssignments ?? [];
  if (assigns.length === 0) return [];
  const items: ListItem[] = assigns.map((a) => ({ range: a.range }));
  return [planListInsert(ctx, items, `${t} = ${v}`, undefined, 'add-merge-update-assignment')];
}

// remove-merge-update-assignment -------------------------------------------
function planAssignmentRemove(
  ctx: EditCtx,
  branch: MergeBranch,
  index: number,
): readonly PlannedByteEdit[] {
  const assigns = branch.updateAssignments ?? [];
  if (assigns.length === 0) return [];
  const items: ListItem[] = assigns.map((a) => ({ range: a.range }));
  const planned = planListRemove(ctx, items, index, 'remove-merge-update-assignment');
  return planned ? [planned] : [];
}
